/**
 * "Save as template" (ADR-0013).
 *
 * Shown when the server says the caller may contribute to the workspace's shelf,
 * which since ADR-0017 is every member -- the engineer holding the firm's standard
 * sheet, not only the office manager. The client never derives this from a role;
 * `capabilities` comes back with the catalogue, resolved per request like `can_edit`
 * on a sheet.
 *
 * The wording is careful about one thing: this is a *copy*. An author who thinks the
 * template tracks the sheet will not understand why editing afterwards changes
 * nothing for colleagues.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { LayoutTemplate } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldLabel } from '@/components/ui/field'
import { FieldInput } from '@/components/ui/input'
import { useToast } from '@/components/ui/toast'
import { listTemplates, saveAsTemplate, TEMPLATES_QUERY_KEY } from '../templates'

export function TemplateControl({
  sheetId,
  defaultTitle,
  onBeforeSave,
}: {
  sheetId: number
  defaultTitle: string
  /** Flushes any debounced autosave. The server copies the document it *holds*, so
   *  saving before the autosave lands would freeze the previous content. */
  onBeforeSave: () => Promise<void>
}) {
  const { t } = useTranslation(['sheets', 'common'])
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('')

  const catalogue = useQuery({
    queryKey: TEMPLATES_QUERY_KEY,
    queryFn: ({ signal }) => listTemplates(signal),
  })

  const save = useMutation({
    mutationFn: async () => {
      await onBeforeSave()
      return saveAsTemplate({
        sheet_id: sheetId,
        title: title.trim(),
        description: description.trim(),
        category: category.trim() || undefined,
      })
    },
    onSuccess: () => {
      setOpen(false)
      setTitle('')
      setDescription('')
      setCategory('')
      void queryClient.invalidateQueries({ queryKey: TEMPLATES_QUERY_KEY })
      toast({ title: t('sheets:sheets.templates.save.saved'), variant: 'success' })
    },
    onError: (error) =>
      toast({
        title: t('common:common.error'),
        description: error instanceof ApiError ? error.localised : undefined,
        variant: 'error',
      }),
  })

  if (!catalogue.data?.capabilities.can_create) return null

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          setTitle((current) => current || defaultTitle)
          setOpen(true)
        }}
        data-testid="save-as-template"
      >
        <LayoutTemplate className="h-3.5 w-3.5" aria-hidden />
        <span className="hidden sm:inline">{t('sheets:sheets.templates.save.action')}</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('sheets:sheets.templates.save.title')}</DialogTitle>
          </DialogHeader>

          <p className="text-sm leading-relaxed text-text-muted">
            {t('sheets:sheets.templates.save.explainer')}
          </p>

          <Field>
            <FieldLabel>{t('sheets:sheets.templates.save.name')}</FieldLabel>
            <FieldInput
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              maxLength={200}
            />
          </Field>

          <Field>
            <FieldLabel>{t('sheets:sheets.templates.save.description')}</FieldLabel>
            <FieldInput
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={t('sheets:sheets.templates.save.description_placeholder')}
              maxLength={2000}
            />
          </Field>

          {/* A free tag, not one of our translated categories: the fixed taxonomy
              applies to what we ship, a firm's shelf is its own (ADR-0017). */}
          <Field>
            <FieldLabel>{t('sheets:sheets.templates.save.category')}</FieldLabel>
            <FieldInput
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              placeholder={t('sheets:sheets.templates.save.category_placeholder')}
              list="workspace-template-categories"
              maxLength={60}
            />
            <datalist id="workspace-template-categories">
              {[
                ...new Set(
                  (catalogue.data?.workspace ?? [])
                    .map((row) => row.category)
                    .filter((value): value is string => Boolean(value)),
                ),
              ]
                .sort()
                .map((value) => (
                  <option key={value} value={value} />
                ))}
            </datalist>
          </Field>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {t('sheets:sheets.templates.cancel')}
            </Button>
            <Button
              onClick={() => save.mutate()}
              loading={save.isPending}
              disabled={title.trim() === ''}
            >
              {t('sheets:sheets.templates.save.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
